import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PlanoSeguro {
  id: string;
  nome: string;
  descricao?: string;
  valor_por_painel?: number;
  cobertura_detalhes?: any;
  vigencia_meses: number;
  inclui_limpeza?: boolean;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export function usePlanos() {
  return useQuery({
    queryKey: ['planos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('planos_seguro')
        .select('*')
        .order('nome');

      if (error) {
        console.error('Erro ao buscar planos:', error);
        throw error;
      }

      return data as PlanoSeguro[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePlanosAtivos() {
  return useQuery({
    queryKey: ['planos-ativos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('planos_seguro')
        .select('*')
        .eq('ativo', true)
        .order('nome');

      if (error) {
        console.error('Erro ao buscar planos ativos:', error);
        throw error;
      }

      return data as PlanoSeguro[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePlano(id: string) {
  return useQuery({
    queryKey: ['plano', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('planos_seguro')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Erro ao buscar plano:', error);
        throw error;
      }

      return data as PlanoSeguro;
    },
    enabled: !!id,
  });
}

export function useCriarPlano() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planoData: {
      nome: string;
      descricao?: string;
      valor_por_painel?: number;
      cobertura_detalhes?: any;
      vigencia_meses?: number;
    }) => {
      const { data, error } = await supabase
        .from('planos_seguro')
        .insert(planoData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planos'] });
    }
  });
}

export function useAtualizarPlano() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { 
      id: string; 
      nome?: string;
      descricao?: string;
      valor_por_painel?: number;
      cobertura_detalhes?: any;
      vigencia_meses?: number;
      ativo?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('planos_seguro')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planos'] });
    }
  });
}